// src/pages/products/ProductDetail.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  FormHelperText,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
  FormControlLabel,
  Switch,
  Card,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Tooltip,
  InputAdornment
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  Add as AddIcon,
  Star as StarIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Inventory as InventoryIcon,
  Euro as EuroIcon,
  Link as LinkIcon
} from '@mui/icons-material';
  import { doc, getDoc, updateDoc, deleteDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
  import { db } from '../../../firebase';
  import { uploadToGarage, useGarageUrls } from '../../../garage';
  import { useDropzone } from 'react-dropzone';
  
  const ProductDetail = () => {
    const { productId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [showSuccess, setShowSuccess] = useState(false);
    const [error, setError] = useState('');
    const [uploading, setUploading] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    const [formData, setFormData] = useState({
      nombre: '',
      descripción: '',
      precio: '',
      categoría: 'otros',
      imágenes: [],
      stock: 0,
      tallas: [],
      colores: [],
      active: true,
      destacado: false,
      paypalLink: ''
    });

    const imageUrls = useGarageUrls(formData.imágenes);

    const [tallaInput, setTallaInput] = useState('');
    const [colorInput, setColorInput] = useState('');
    const [formErrors, setFormErrors] = useState({});
    
    useEffect(() => {
      const fetchProduct = async () => {
        try {
          const docRef = doc(db, 'products', productId);
          const docSnap = await getDoc(docRef);
          
          if (docSnap.exists()) {
            const data = docSnap.data();
            setFormData({
              ...data,
              precio: (data.precio !== undefined && data.precio !== null) ? data.precio.toString() : '',
              stock: data.stock ? data.stock.toString() : '0'
            });
          } else {
            setError('No se encontró el producto');
            navigate('/admin/products');
          }
        } catch (error) {
          console.error('Error fetching product:', error);
          setError('Error al cargar el producto');
        } finally {
          setFetching(false);
        }
      };
  
      fetchProduct();
    }, [productId, navigate]);
    
    const { getRootProps, getInputProps } = useDropzone({
      accept: {
        'image/*': ['.jpeg', '.jpg', '.png', '.gif']
      },
      onDrop: acceptedFiles => {
        handleImageUpload(acceptedFiles);
      }
    });
    
    const handleChange = (e) => {
      const { name, value } = e.target;
      
      // Handle numeric values
      if (name === 'precio') {
        // Allow only numeric values and a single decimal point
        const regex = /^$|^[0-9]+(\.[0-9]{0,2})?$/;
        if (regex.test(value)) {
          setFormData({
            ...formData,
            [name]: value
          });
        }
      } else if (name === 'stock') {
        // Allow only integers
        const regex = /^$|^[0-9]+$/;
        if (regex.test(value)) {
          setFormData({
            ...formData,
            [name]: value
          });
        }
      } else {
        // Handle other fields
        setFormData({
          ...formData,
          [name]: value
        });
      }
      
      // Clear error when field is edited
      if (formErrors[name]) {
        setFormErrors({
          ...formErrors,
          [name]: ''
        });
      }
    };
    
    const handleSwitchChange = (e) => {
      const { name, checked } = e.target;
      setFormData({
        ...formData,
        [name]: checked
      });
    };
    
    const handleAddTalla = () => {
      if (!tallaInput.trim()) {
        setFormErrors({
          ...formErrors,
          tallas: 'La talla no puede estar vacía'
        });
        return;
      }
      
      if (!(formData.tallas || []).includes(tallaInput)) {
        setFormData({
          ...formData,
          tallas: [...(formData.tallas || []), tallaInput]
        });
      }
      
      setTallaInput('');
      
      // Clear error
      if (formErrors.tallas) {
        setFormErrors({
          ...formErrors,
          tallas: ''
        });
      }
    };
    
    const handleAddColor = () => {
      if (!colorInput.trim()) {
        setFormErrors({
          ...formErrors,
          colores: 'El color no puede estar vacío'
        });
        return;
      }
      
      if (!(formData.colores || []).includes(colorInput)) {
        setFormData({
          ...formData,
          colores: [...(formData.colores || []), colorInput]
        });
      }
      
      setColorInput('');
      
      // Clear error
      if (formErrors.colores) {
        setFormErrors({
          ...formErrors,
          colores: ''
        });
      }
    };
    
    const handleRemoveTalla = (talla) => {
      setFormData({
        ...formData,
        tallas: (formData.tallas || []).filter(t => t !== talla)
      });
    };
    
    const handleRemoveColor = (color) => {
      setFormData({
        ...formData,
        colores: (formData.colores || []).filter(c => c !== color)
      });
    };
    
    const handleRemoveImage = (index) => {
      const newImages = [...(formData.imágenes || [])];
      newImages.splice(index, 1);
      setFormData({
        ...formData,
        imágenes: newImages
      });
    };
    
    const handleImageUpload = async (files) => {
      if (files.length === 0) return;

      setUploading(true);
      setError('');

      try {
        const imageUrls = await Promise.all(
          files.map(file => uploadToGarage(file, 'products'))
        );
        setFormData({ ...formData, imágenes: [...(formData.imágenes || []), ...imageUrls] });
      } catch (error) {
        console.error('Error uploading images:', error);
        setError('Error al subir las imágenes. Inténtalo de nuevo.');
      } finally {
        setUploading(false);
      }
    };
    
    const validateForm = () => {
      const errors = {};
      
      if (!formData.nombre) {
        errors.nombre = 'El nombre es obligatorio';
      }
      
      if (formData.precio === '' || formData.precio === undefined || formData.precio === null) {
        errors.precio = 'El precio es obligatorio';
      }

      if (formData.paypalLink && !formData.paypalLink.startsWith('https://')) {
        errors.paypalLink = 'El enlace de PayPal debe empezar con https://';
      }

      if (!formData.categoría) {
        errors.categoría = 'La categoría es obligatoria';
      }
      
      setFormErrors(errors);
      return Object.keys(errors).length === 0;
    };
    
    const handleSubmit = async (e) => {
      e.preventDefault();
      
      if (!validateForm()) {
        return;
      }
      
      setLoading(true);
      setError('');
      
      try {
        // Format data for Firestore
        const productData = {
          ...formData,
          precio: parseFloat(formData.precio),
          stock: parseInt(formData.stock, 10),
          updatedAt: serverTimestamp()
        };
        
        await updateDoc(doc(db, 'products', productId), productData);
        setShowSuccess(true);
      } catch (error) {
        console.error('Error updating product:', error);
        setError('Error al actualizar el producto. Inténtalo de nuevo.');
      } finally {
        setLoading(false);
      }
    };
    
    const handleDeleteDialogOpen = () => {
      setDeleteDialogOpen(true);
    };
    
    const handleDeleteDialogClose = () => {
      setDeleteDialogOpen(false);
    };
    
    const handleDeleteProduct = async () => {
      try {
        await deleteDoc(doc(db, 'products', productId));
        navigate('/admin/products');
      } catch (error) {
        console.error('Error deleting product:', error);
        setError('Error al eliminar el producto');
        setDeleteDialogOpen(false);
      }
    };
    
    if (fetching) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
        </Box>
      );
    }
    
    const stockNum = parseInt(formData.stock, 10) || 0;
    const stockColor = stockNum === 0 ? 'error' : stockNum <= 5 ? 'warning' : 'success';
    const stockLabel = stockNum === 0 ? 'Sin stock' : stockNum <= 5 ? 'Stock bajo' : 'En stock';

    return (
      <>
      <Box sx={{ flexGrow: 1, p: { xs: 1, sm: 2 } }}>

        {/* ── Cabecera ── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
          <Tooltip title="Volver al catálogo">
            <IconButton onClick={() => navigate('/admin/products')} size="small">
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h5" fontWeight={700} noWrap>
              {formData.nombre || 'Editar producto'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ID {productId?.slice(0, 10)}…
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexShrink={0}>
            <Chip
              icon={formData.active ? <CheckCircleIcon /> : <CancelIcon />}
              label={formData.active ? 'Activo' : 'Inactivo'}
              color={formData.active ? 'success' : 'default'}
              size="small"
            />
            {formData.destacado && (
              <Chip icon={<StarIcon />} label="Destacado" color="warning" size="small" />
            )}
            <Chip
              icon={<InventoryIcon />}
              label={stockLabel}
              color={stockColor}
              size="small"
              variant="outlined"
            />
          </Stack>
        </Box>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3} alignItems="flex-start">

            {/* ══════════════════════ COLUMNA PRINCIPAL ══════════════════════ */}
            <Grid item xs={12} lg={8}>

              {/* ── Información básica ── */}
              <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} color="text.secondary" gutterBottom>
                  INFORMACIÓN DEL PRODUCTO
                </Typography>
                <Divider sx={{ mb: 2.5 }} />
                <Grid container spacing={2.5}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Nombre del producto"
                      name="nombre"
                      value={formData.nombre || ''}
                      onChange={handleChange}
                      error={!!formErrors.nombre}
                      helperText={formErrors.nombre}
                      required
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Descripción"
                      name="descripción"
                      value={formData.descripción || ''}
                      onChange={handleChange}
                      multiline
                      rows={4}
                      variant="outlined"
                      placeholder="Describe el producto: materiales, uso, detalles relevantes…"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Enlace de pago PayPal"
                      name="paypalLink"
                      value={formData.paypalLink || ''}
                      onChange={handleChange}
                      placeholder="https://paypal.me/…"
                      error={!!formErrors.paypalLink}
                      helperText={formErrors.paypalLink}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LinkIcon fontSize="small" color="action" />
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* ── Tallas y colores ── */}
              <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} color="text.secondary" gutterBottom>
                  VARIANTES
                </Typography>
                <Divider sx={{ mb: 2.5 }} />
                <Grid container spacing={3}>

                  {/* Tallas */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" fontWeight={600} gutterBottom>Tallas disponibles</Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                      <TextField
                        size="small"
                        label="Nueva talla"
                        value={tallaInput}
                        onChange={(e) => setTallaInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTalla())}
                        error={!!formErrors.tallas}
                        helperText={formErrors.tallas}
                        sx={{ flexGrow: 1 }}
                      />
                      <Button variant="outlined" size="small" onClick={handleAddTalla} sx={{ whiteSpace: 'nowrap' }}>
                        <AddIcon fontSize="small" />
                      </Button>
                    </Box>
                    {/* Accesos rápidos */}
                    <Stack direction="row" flexWrap="wrap" gap={0.5} mb={1.5}>
                      {['XS','S','M','L','XL','XXL'].map(s => (
                        <Chip
                          key={s} label={s} size="small" variant="outlined"
                          clickable
                          onClick={() => {
                            if (!(formData.tallas || []).includes(s)) {
                              setFormData(p => ({ ...p, tallas: [...(p.tallas||[]), s] }));
                            }
                          }}
                          sx={{ fontSize: 11, height: 22 }}
                        />
                      ))}
                    </Stack>
                    <Stack direction="row" flexWrap="wrap" gap={0.75}>
                      {(formData.tallas || []).length === 0
                        ? <Typography variant="caption" color="text.disabled">Sin tallas añadidas</Typography>
                        : (formData.tallas || []).map((t, i) => (
                            <Chip key={i} label={t} onDelete={() => handleRemoveTalla(t)} size="small" color="primary" variant="outlined" />
                          ))
                      }
                    </Stack>
                  </Grid>

                  {/* Colores */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" fontWeight={600} gutterBottom>Colores disponibles</Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                      <TextField
                        size="small"
                        label="Nuevo color"
                        value={colorInput}
                        onChange={(e) => setColorInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddColor())}
                        error={!!formErrors.colores}
                        helperText={formErrors.colores}
                        sx={{ flexGrow: 1 }}
                      />
                      <Button variant="outlined" size="small" onClick={handleAddColor} sx={{ whiteSpace: 'nowrap' }}>
                        <AddIcon fontSize="small" />
                      </Button>
                    </Box>
                    <Stack direction="row" flexWrap="wrap" gap={0.75} mt={1}>
                      {(formData.colores || []).length === 0
                        ? <Typography variant="caption" color="text.disabled">Sin colores añadidos</Typography>
                        : (formData.colores || []).map((c, i) => (
                            <Chip
                              key={i}
                              label={c}
                              onDelete={() => handleRemoveColor(c)}
                              size="small"
                              variant="outlined"
                              avatar={
                                <Box sx={{
                                  width: 14, height: 14, borderRadius: '50%',
                                  bgcolor: c, border: '1px solid rgba(0,0,0,.15)',
                                  flexShrink: 0
                                }} />
                              }
                            />
                          ))
                      }
                    </Stack>
                  </Grid>
                </Grid>
              </Paper>

              {/* ── Imágenes ── */}
              <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="subtitle1" fontWeight={700} color="text.secondary">
                    IMÁGENES
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    {(formData.imágenes || []).length} imagen{(formData.imágenes||[]).length !== 1 ? 'es' : ''}
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2.5 }} />

                {/* Imagen principal grande */}
                {imageUrls.length > 0 && (
                  <Box sx={{ position: 'relative', mb: 2, borderRadius: 2, overflow: 'hidden', bgcolor: '#f5f5f5' }}>
                    <Box
                      component="img"
                      src={imageUrls[0]}
                      alt="Imagen principal"
                      sx={{ width: '100%', maxHeight: 320, objectFit: 'contain', display: 'block' }}
                    />
                    <Chip
                      label="Principal"
                      size="small"
                      color="primary"
                      sx={{ position: 'absolute', top: 10, left: 10, fontWeight: 600 }}
                    />
                    <Tooltip title="Eliminar imagen principal">
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveImage(0)}
                        sx={{
                          position: 'absolute', top: 8, right: 8,
                          bgcolor: 'rgba(0,0,0,0.55)', color: '#fff',
                          '&:hover': { bgcolor: 'error.main' }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}

                {/* Miniaturas adicionales */}
                {imageUrls.length > 1 && (
                  <Stack direction="row" spacing={1} sx={{ mb: 2, overflowX: 'auto', pb: 1 }}>
                    {imageUrls.slice(1).map((url, i) => (
                      <Box
                        key={i + 1}
                        sx={{
                          position: 'relative', flexShrink: 0, width: 90, height: 90,
                          borderRadius: 1.5, overflow: 'hidden', bgcolor: '#f5f5f5',
                          border: '1px solid', borderColor: 'divider',
                          '&:hover .del-btn': { opacity: 1 }
                        }}
                      >
                        <Box
                          component="img"
                          src={url}
                          alt={`Imagen ${i + 2}`}
                          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                        <IconButton
                          className="del-btn"
                          size="small"
                          onClick={() => handleRemoveImage(i + 1)}
                          sx={{
                            position: 'absolute', top: 2, right: 2, opacity: 0,
                            transition: 'opacity .2s',
                            bgcolor: 'rgba(0,0,0,0.55)', color: '#fff', p: 0.3,
                            '&:hover': { bgcolor: 'error.main' }
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>
                    ))}
                  </Stack>
                )}

                {/* Zona de drop */}
                <Box
                  {...getRootProps()}
                  sx={{
                    border: '2px dashed',
                    borderColor: 'divider',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all .2s',
                    '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50' }
                  }}
                >
                  <input {...getInputProps()} />
                  {uploading ? (
                    <Stack alignItems="center" spacing={1}>
                      <CircularProgress size={28} />
                      <Typography variant="body2" color="text.secondary">Subiendo…</Typography>
                    </Stack>
                  ) : (
                    <Stack alignItems="center" spacing={0.5}>
                      <CloudUploadIcon sx={{ fontSize: 36, color: 'text.disabled' }} />
                      <Typography variant="body2" fontWeight={500}>
                        Arrastra imágenes o haz clic para añadir
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        JPG, PNG, GIF
                      </Typography>
                    </Stack>
                  )}
                </Box>
              </Paper>
            </Grid>

            {/* ══════════════════════ SIDEBAR ══════════════════════ */}
            <Grid item xs={12} lg={4}>
              <Box sx={{ position: { lg: 'sticky' }, top: { lg: 16 } }}>

                {/* ── Acciones ── */}
                <Paper sx={{ p: 2.5, mb: 2, borderRadius: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    size="large"
                    startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                    disabled={loading}
                    sx={{ mb: 1.5, fontWeight: 700 }}
                  >
                    {loading ? 'Guardando…' : 'Guardar cambios'}
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => navigate('/admin/products')}
                    disabled={loading}
                  >
                    Cancelar
                  </Button>
                </Paper>

                {/* ── Precio y stock ── */}
                <Paper sx={{ p: 2.5, mb: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} color="text.secondary" gutterBottom>
                    PRECIO Y STOCK
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      label="Precio"
                      name="precio"
                      value={formData.precio || ''}
                      onChange={handleChange}
                      error={!!formErrors.precio}
                      helperText={formErrors.precio}
                      required
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EuroIcon fontSize="small" color="action" />
                          </InputAdornment>
                        )
                      }}
                    />
                    <Box>
                      <TextField
                        fullWidth
                        label="Stock"
                        name="stock"
                        value={formData.stock || '0'}
                        onChange={handleChange}
                        InputProps={{ inputProps: { min: 0 } }}
                      />
                      <Box sx={{ mt: 1 }}>
                        <Chip
                          label={`${stockNum} ud. · ${stockLabel}`}
                          color={stockColor}
                          size="small"
                          variant="outlined"
                          icon={<InventoryIcon />}
                        />
                      </Box>
                    </Box>
                  </Stack>
                </Paper>

                {/* ── Categoría ── */}
                <Paper sx={{ p: 2.5, mb: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} color="text.secondary" gutterBottom>
                    CATEGORÍA
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <FormControl fullWidth error={!!formErrors.categoría}>
                    <InputLabel>Categoría</InputLabel>
                    <Select
                      name="categoría"
                      value={formData.categoría || 'otros'}
                      onChange={handleChange}
                      label="Categoría"
                    >
                      <MenuItem value="uniforme">Uniforme</MenuItem>
                      <MenuItem value="material">Material escolar</MenuItem>
                      <MenuItem value="libros">Libros</MenuItem>
                      <MenuItem value="otros">Otros</MenuItem>
                    </Select>
                    {formErrors.categoría && <FormHelperText>{formErrors.categoría}</FormHelperText>}
                  </FormControl>
                </Paper>

                {/* ── Estado ── */}
                <Paper sx={{ p: 2.5, mb: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} color="text.secondary" gutterBottom>
                    ESTADO
                  </Typography>
                  <Divider sx={{ mb: 1 }} />
                  <Box sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    py: 1.5, px: 1, borderRadius: 1,
                    bgcolor: formData.active ? 'success.50' : 'grey.50'
                  }}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>Producto activo</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formData.active ? 'Visible en la app' : 'Oculto en la app'}
                      </Typography>
                    </Box>
                    <Switch
                      checked={formData.active || false}
                      onChange={handleSwitchChange}
                      name="active"
                      color="success"
                    />
                  </Box>
                  <Box sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    py: 1.5, px: 1, mt: 0.5, borderRadius: 1,
                    bgcolor: formData.destacado ? 'warning.50' : 'grey.50'
                  }}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>Producto destacado</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formData.destacado ? 'Aparece en sección destacados' : 'No destacado'}
                      </Typography>
                    </Box>
                    <Switch
                      checked={formData.destacado || false}
                      onChange={handleSwitchChange}
                      name="destacado"
                      color="warning"
                    />
                  </Box>
                </Paper>

                {/* ── Zona de peligro ── */}
                <Paper sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'error.200' }}>
                  <Typography variant="subtitle1" fontWeight={700} color="error" gutterBottom>
                    ZONA DE PELIGRO
                  </Typography>
                  <Divider sx={{ mb: 2, borderColor: 'error.100' }} />
                  <Button
                    variant="outlined"
                    color="error"
                    fullWidth
                    startIcon={<DeleteIcon />}
                    onClick={handleDeleteDialogOpen}
                    disabled={loading}
                  >
                    Eliminar producto
                  </Button>
                </Paper>

              </Box>
            </Grid>
          </Grid>
        </form>
      </Box>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteDialogClose}
        >
          <DialogTitle>¿Eliminar producto?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              ¿Estás seguro de que quieres eliminar el producto "{formData.nombre}"? Esta acción no se puede deshacer.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteDialogClose}>Cancelar</Button>
            <Button onClick={handleDeleteProduct} color="error" autoFocus>
              Eliminar
            </Button>
          </DialogActions>
        </Dialog>
        
        <Snackbar
          open={showSuccess}
          autoHideDuration={4000}
          onClose={() => setShowSuccess(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity="success">
            Producto actualizado exitosamente
          </Alert>
        </Snackbar>
        
        <Snackbar
          open={!!error}
          autoHideDuration={4000}
          onClose={() => setError('')}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity="error">
            {error}
          </Alert>
        </Snackbar>
      </>
    );
  };
  
  export default ProductDetail;